import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { formatBytes } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ExternalLink,
  FolderOpen,
  Loader2,
  AlertTriangle,
  Copy,
  Check,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useOpportunityDriveFiles, useSetOpportunityDriveFolder } from '../../hooks/useOpportunityDriveFiles';
import type { DriveFile } from '@/features/accounts/api';

interface OpportunityDriveFolderSectionProps {
  opportunityId: string;
}

function extractFolderId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const urlMatch = trimmed.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (urlMatch) return urlMatch[1]!;
  const queryMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (queryMatch) return queryMatch[1]!;
  if (/^[a-zA-Z0-9_-]{10,}$/.test(trimmed)) return trimmed;
  return null;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CopyableEmail({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(email); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { toast.error('Failed to copy'); }
      }}
      className="inline-flex items-center gap-1.5 rounded border bg-background px-2 py-0.5 font-mono text-xs hover:bg-muted"
    >
      <span>{email}</span>
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function FolderForm({
  opportunityId,
  initial,
  onDone,
}: {
  opportunityId: string;
  initial?: string | null;
  onDone?: () => void;
}) {
  const [input, setInput] = useState(initial ?? '');
  const setFolder = useSetOpportunityDriveFolder(opportunityId);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      await setFolder.mutateAsync(null);
      onDone?.();
      return;
    }
    const id = extractFolderId(trimmed);
    if (!id) {
      toast.error("That doesn't look like a Drive folder URL or ID.");
      return;
    }
    await setFolder.mutateAsync(id);
    onDone?.();
  };

  return (
    <form onSubmit={handleSave} className="space-y-2">
      <Label htmlFor="opp-drive-folder-input" className="text-xs">Drive folder URL or ID</Label>
      <div className="flex gap-2">
        <Input
          id="opp-drive-folder-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..."
          className="text-sm"
        />
        <Button type="submit" size="sm" disabled={setFolder.isPending}>
          {setFolder.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
        </Button>
        {onDone && (
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
        )}
      </div>
    </form>
  );
}

function DriveFileRow({ file }: { file: DriveFile }) {
  const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
  const meta = [formatDate(file.modifiedTime), file.size ? formatBytes(Number(file.size)) : ''].filter(Boolean).join(' · ');
  return (
    <a
      href={file.webViewLink ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors"
    >
      {file.iconLink
        ? <img src={file.iconLink} alt="" className="h-4 w-4 shrink-0" />
        : <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />}
      <span className="text-sm flex-1 truncate text-foreground">
        {file.name}
        {isFolder && <span className="text-xs text-muted-foreground"> (folder)</span>}
      </span>
      {meta && <span className="text-xs text-muted-foreground shrink-0">{meta}</span>}
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
    </a>
  );
}

export function OpportunityDriveFolderSection({ opportunityId }: OpportunityDriveFolderSectionProps) {
  const { data, isLoading, isError, error } = useOpportunityDriveFiles(opportunityId);
  const [editing, setEditing] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const setFolder = useSetOpportunityDriveFolder(opportunityId);

  const status = data?.status;
  const serviceAccountEmail = data?.serviceAccountEmail;

  const headerActions = status === 'ok' && !editing ? (
    <div className="flex items-center gap-1">
      {data?.folderUrl && (
        <Button asChild size="sm" variant="ghost">
          <a href={data.folderUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in Drive
          </a>
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
        <Pencil className="h-3.5 w-3.5 mr-1" /> Change
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirmUnlink(true)} disabled={setFolder.isPending}>
        <Trash2 className="h-3.5 w-3.5 mr-1" /> Unlink
      </Button>
    </div>
  ) : null;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Google Drive Folder</CardTitle>
          <CardDescription>Read-only view of the Drive folder attached to this opportunity.</CardDescription>
        </div>
        {headerActions}
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {isError && !isLoading && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not load Drive files</AlertTitle>
            <AlertDescription>{error instanceof Error ? error.message : 'Unknown error'}</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && status === 'not_configured' && !editing && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">No Drive folder attached to this opportunity yet.</p>
            <FolderForm opportunityId={opportunityId} />
            {serviceAccountEmail && (
              <p className="text-xs text-muted-foreground">
                After setting a folder, share it with <CopyableEmail email={serviceAccountEmail} /> (viewer is enough).
              </p>
            )}
          </div>
        )}

        {!isLoading && !isError && editing && (
          <FolderForm opportunityId={opportunityId} initial={data?.folderId ?? ''} onDone={() => setEditing(false)} />
        )}

        {!isLoading && !isError && !editing && status === 'forbidden' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Folder not accessible</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="text-sm">The folder is set, but the service account can't read it yet.</p>
              {serviceAccountEmail && (
                <p className="text-sm">Share the folder with <CopyableEmail email={serviceAccountEmail} /> (Viewer access is enough), then reload.</p>
              )}
              <div className="flex gap-2 pt-1">
                {data?.folderUrl && (
                  <Button asChild size="sm" variant="outline">
                    <a href={data.folderUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open folder
                    </a>
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Change folder</Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !editing && status === 'not_found' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Folder not found</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="text-sm">The folder ID on this opportunity doesn't resolve. It may have been deleted or the ID is wrong.</p>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Change folder</Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !editing && status === 'ok' && (
          data!.files.length === 0
            ? <p className="text-sm text-muted-foreground">This folder is empty.</p>
            : (
              <div className="space-y-0.5">
                {data!.files.map((f) => <DriveFileRow key={f.id} file={f} />)}
              </div>
            )
        )}
      </CardContent>

      <AlertDialog open={confirmUnlink} onOpenChange={setConfirmUnlink}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Drive folder?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the folder reference from the opportunity. The folder itself is not touched in Google Drive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { await setFolder.mutateAsync(null); setConfirmUnlink(false); }}>
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
